import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import store from '@/store';
import { ErrorResponse } from '@/utils/response';
import { axios, CustomAxiosResponse } from '@/utils/axios';
import { Collection } from './collection.model';
import { QuestionLanguage } from '../subject/subject.model';

export interface CollectionStoreInterface {
  collections: Collection[];
  collection: Collection | null;
  error: ErrorResponse | null;
  fetchCollections(): Promise<void>;
  createCollection(params: CreateCollectionParams): Promise<void>;
  addQuestion(params: AddQuestionParams): Promise<void>;
  autoAddQuestions(params: AutoAddQuestionsParams): Promise<void>;
}

export interface CreateCollectionParams {
  name: string;
  subjectUuid: string;
}

export interface AutoAddQuestionsParams {
  collectionUuid: string;
  chapterId: number;
}
export interface AddQuestionParams {
  questionId: number;
  collectionUuid: string;
  answerPosition: number;
  isSubjective: boolean;
  languages: QuestionLanguage[];
}

@Module({
  namespaced: true,
  dynamic: true,
  name: 'collection',
  store,
})
class CollectionStore extends VuexModule {
  collections: Collection[] = [];
  collection: Collection | null = null;
  error: ErrorResponse | null = null;

  @Mutation
  updateCollections(params: Collection[]) {
    this.collections = params;
  }

  @Mutation
  updateCollection(params: Collection) {
    this.collection = params;
  }

  @Mutation
  setError(params: ErrorResponse) {
    this.error = params;
  }

  @Action
  async fetchCollections(): Promise<void> {
    this.context.commit('updateCollections', []);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(`collections/me`);

      this.context.commit('updateCollections', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async createCollection(params: CreateCollectionParams): Promise<void> {
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.post(`collections`, {
        ...params,
      });

      this.context.commit('updateCollection', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async addQuestion(params: AddQuestionParams): Promise<void> {
    this.context.commit('setError', null);

    try {
      await axios.post(`/collection-items`, params);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async autoAddQuestions(params: AutoAddQuestionsParams): Promise<void> {
    this.context.commit('setError', null);

    try {
      await axios.post(`/collection-items/auto`, params);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }
}

export default getModule(CollectionStore);
